import { Component } from "../base/Component";
import { ComponentFactory } from "../../service/ComponentFactory";
import { PageContext } from "../../service/PageContext";

type RelatedItem = {
    title: string;
    url: string;
    posteddate: string;
    engagementtype: string;
    engagementtime: number;
    video: string;
};

export class ListenNextContentDiscoverability extends Component {
    private static healioUrl = "https://www.healio.com";
    private static podcastMode = "podcast";
    private cleanArticleId: string | undefined;
    private primarySpecialtyName: string | undefined;
    private primarySubSpecialtyId: string | undefined;
    private seedExcludeUrl: string | undefined;
    private hwsHost: string | undefined;
    private visitedPagesArray: string[] | null;
    private readLoaded: boolean | undefined;

    constructor(element: HTMLElement, context: PageContext) {
        super(element, context);
        this.cleanArticleId = this._element.dataset.cleanArticleId;
        this.primarySpecialtyName = this._element.dataset.primarySpecName;
        this.primarySubSpecialtyId = this._element.dataset.primarySubSpecId;
        this.seedExcludeUrl = this._element.dataset.seedExcludeUrl;
        this.hwsHost = this._element.dataset.hwsHost;
        this.visitedPagesArray = null;
        this.readLoaded = false;
    }

    public async init(): Promise<void> {
        const component = this;
        console.log("[ListenNext] init", {
            cleanArticleId: this.cleanArticleId,
            primarySpecialtyName: this.primarySpecialtyName,
            primarySubSpecialtyId: this.primarySubSpecialtyId,
            seedExcludeUrl: this.seedExcludeUrl,
            hasVisitor: typeof window.Visitor !== "undefined" && window.Visitor !== "",
            hasContext: typeof window.Context !== "undefined"
        });
        $(async () => {
            if (window.Visitor !== undefined && window.Visitor !== "") {
                console.log("[ListenNext] Visitor ready, loading now");
                component.loadListenNext();
            } else {
                // Some podcast episode pages appear to miss/beat this event timing.
                // Keep event binding, but also add a delayed fallback call.
                console.log("[ListenNext] Visitor not ready, waiting for globalsloaded + timeout fallback");
                $(window).one("globalsloaded", () => component.loadListenNext());
                window.setTimeout(() => component.loadListenNext(), 1500);
            }
        });
    }

    private filterAlreadyRead(this: ListenNextContentDiscoverability, value: RelatedItem): any {
        if (this.visitedPagesArray && this.visitedPagesArray.includes("{" + value.url.replace("/find/", "").toUpperCase() + "}")) {
            return false;
        } else {
            return true;
        }
    }

    private filterResponse(input: any): RelatedItem[] {
        return input.relatedItems.filter((value: RelatedItem) => this.filterAlreadyRead(value));
    }

    private normalizeUrl(url: string | undefined): string {
        if (!url) return "";
        return url.toString().replace(ListenNextContentDiscoverability.healioUrl, "").trim().toLowerCase();
    }

    private loadPrimaryRecommendation(primaryspecname: string, primarysubspec: string, excludedUrls: Set<string>): void {
        const component = this;
        $.ajax({
            type: "GET",
            url: component.hwsHost + "/~/hws/relatedchips/" + component.cleanArticleId + "/" + ListenNextContentDiscoverability.podcastMode + "/" + primaryspecname + "/" + primarysubspec,
            success: function (msg: any): void {
                const relatedItems: RelatedItem[] = msg?.response?.relatedItems || [];
                const nextPrimary = relatedItems.find((candidate: RelatedItem) => {
                    const normalized = component.normalizeUrl(candidate?.url);
                    return normalized !== "" && !excludedUrls.has(normalized);
                });
                const fallbackPrimary = relatedItems.find((candidate: RelatedItem) => {
                    return component.normalizeUrl(candidate?.url) !== "";
                });
                const finalPrimary = nextPrimary || fallbackPrimary;

                if (finalPrimary?.url) {
                    const primaryLink = $("#recoPrimary a");
                    primaryLink.attr("href", finalPrimary.url.toString().replace(ListenNextContentDiscoverability.healioUrl, ""));
                    primaryLink.text("Continue listening");
                    console.log("[ListenNext] primary recommendation selected", {
                        strictMatchFound: !!nextPrimary,
                        selectedUrl: finalPrimary.url
                    });
                } else {
                    console.warn("[ListenNext] no primary recommendation available");
                }
            },
            error: function () {
                console.error("Error loading podcast primary recommendation");
            },
            timeout: 5000
        });
    }

    private loadListenNext(): void {
        console.log("[ListenNext] loadListenNext invoked", { readLoaded: this.readLoaded });
        if (this.readLoaded) return;
        this.readLoaded = true;
        const component = this;

        if (typeof (window.Context) === "undefined") {
            console.warn("[ListenNext] window.Context is undefined, aborting");
            return;
        }
        if (window.Visitor && (window.Visitor.isbot || window.Visitor.type === "DC")) {
            console.log("[ListenNext] bot/DC visitor, skipping");
            return;
        }

        const visitedPages = sessionStorage.getItem("visitedPages");
        component.visitedPagesArray = visitedPages ? JSON.parse(visitedPages) : null;
        if (component.visitedPagesArray != null && window.Context?.documentid) {
            if (component.visitedPagesArray.length < 8) {
                if (!component.visitedPagesArray.includes(window.Context.documentid)) {
                    component.visitedPagesArray.push(window.Context.documentid);
                    sessionStorage.setItem("visitedPages", JSON.stringify(component.visitedPagesArray));
                }
            } else {
                component.visitedPagesArray.shift();
                component.visitedPagesArray.push(window.Context.documentid);
                sessionStorage.setItem("visitedPages", JSON.stringify(component.visitedPagesArray));
            }
        } else if (window.Context?.documentid) {
            component.visitedPagesArray = [window.Context.documentid];
            sessionStorage.setItem("visitedPages", JSON.stringify(component.visitedPagesArray));
        }

        let primaryspecname = component.primarySpecialtyName;
        let primarysubspec = component.primarySubSpecialtyId;
        if (!primaryspecname || primaryspecname === "") primaryspecname = "na";
        if (!primarysubspec || primarysubspec === "") primarysubspec = "na";
        primaryspecname = primaryspecname.replace(/&#39;/g, "");
        primaryspecname = primaryspecname.replace(/ &/g, "");
        const requestUrl = component.hwsHost + "/~/hws/relatedchips/" + component.cleanArticleId + "/" + ListenNextContentDiscoverability.podcastMode + "/" + primaryspecname + "/" + primarysubspec;
        console.log("[ListenNext] request", {
            requestUrl: requestUrl,
            visitedCount: component.visitedPagesArray?.length || 0
        });

        $.ajax({
            type: "GET",
            url: requestUrl,
            success: function (msg: any): void {
                let articleContentCount = 4;
                const articleContentElem = document.getElementsByClassName("article__container");
                if (articleContentElem[0]?.clientHeight !== undefined && articleContentElem[0].clientHeight <= 800) {
                    articleContentCount = 3;
                }

                const filteredResponse: RelatedItem[] = component.filterResponse(msg.response);
                console.log("[ListenNext] response meta", {
                    relatedItemsRaw: msg?.response?.relatedItems?.length || 0,
                    filteredResponseCount: filteredResponse?.length || 0,
                    articleContentCount: articleContentCount
                });
                const leftRailItems = filteredResponse.slice(0, articleContentCount);
                const excludedUrls = new Set<string>();
                excludedUrls.add(component.normalizeUrl(window.location.pathname));
                const seedExcludeNormalized = component.normalizeUrl(component.seedExcludeUrl);
                if (seedExcludeNormalized) {
                    excludedUrls.add(seedExcludeNormalized);
                }
                leftRailItems.forEach((item) => excludedUrls.add(component.normalizeUrl(item?.url)));
                console.log("[ListenNext] selection", {
                    leftRailItemsCount: leftRailItems.length,
                    excludedUrls: Array.from(excludedUrls)
                });

                let i = 1;
                do {
                    const nextItem = leftRailItems[i - 1];
                    const cardElem = document.getElementById("rmlrCard" + i);
                    if (!cardElem) continue;

                    if (nextItem && nextItem.url) {
                        const postedDate = new Date(nextItem.posteddate);
                        const formattedPostedDate = postedDate.toLocaleString("en-US", {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                        });

                        cardElem.querySelectorAll(".date")[0].innerHTML = formattedPostedDate;

                        if (nextItem.engagementtime !== null && nextItem.engagementtime !== undefined) {
                            cardElem.querySelectorAll(".time")[0].innerHTML = nextItem.engagementtime + " min listen";
                        } else {
                            cardElem.querySelectorAll(".time")[0].innerHTML = "";
                        }

                        cardElem.querySelectorAll(".card-title a")[0].setAttribute("href", nextItem.url);
                        cardElem.querySelectorAll(".card-title a")[0].innerHTML = nextItem.title;
                        cardElem.classList.remove("d-none");
                        console.log("[ListenNext] card rendered", { index: i, url: nextItem.url, title: nextItem.title });
                    } else {
                        console.warn("[ListenNext] removing empty card", { index: i });
                        cardElem.remove();
                    }
                    i++;
                } while (i <= articleContentCount);

                $("#readMoreLeftRail").removeClass("d-none");
                console.log("[ListenNext] left rail shown");
                component.loadPrimaryRecommendation(primaryspecname, primarysubspec, excludedUrls);
            },
            error: function (xhr: any, status: string, error: string) {
                console.error("[ListenNext] Error loading ListenNext", {
                    status: status,
                    error: error,
                    responseText: xhr?.responseText
                });
            },
            timeout: 5000
        });
    }
}

ComponentFactory.registerComponent("ListenNextContentDiscoverability", ListenNextContentDiscoverability);
